import get from 'lodash/get';
import set from 'lodash/set';
import omit from 'lodash/omit';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import memoize from 'lodash/memoize';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';

import {
  RESET_ANALYTICS_FILTER_SETTINGS_FOR_PAGE,
  UPDATE_ANALYTICS_FILTER_SETTINGS_FOR_PAGE,
  SET_IS_SITE_DETAILS_TAB_LOADING,

  FETCH_SITE_VISITS_CHART_DATA_FOR_SITE_SUCCESS,
  FETCH_SITE_VISITS_CHART_DATA_FOR_SITE_FAILURE,
  FETCH_SITE_VISITS_TABLE_DATA_FOR_SITE_SUCCESS,
  FETCH_SITE_VISITS_TABLE_DATA_FOR_SITE_FAILURE,
  FETCH_NEW_SITE_VISITS_TABLE_DATA_FOR_SITE_SUCCESS,
  FETCH_VISIT_SESSION_DATA_FOR_SITE_REQUEST,
  FETCH_VISIT_SESSION_DATA_FOR_SITE_SUCCESS,
  FETCH_VISIT_SESSION_DATA_FOR_SITE_FAILURE,

  FETCH_TOP_SUBPAGES_DATA_FOR_SITE_REQUEST,
  FETCH_TOP_SUBPAGES_DATA_FOR_SITE_SUCCESS,
  FETCH_TOP_SUBPAGES_DATA_FOR_SITE_FAILURE,
  FETCH_SUBPAGE_DATA_FOR_SITE_REQUEST,
  FETCH_SUBPAGE_DATA_FOR_SITE_SUCCESS,
  FETCH_SUBPAGE_DATA_FOR_SITE_FAILURE,
  FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_REQUEST,
  FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_SUCCESS,
  FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_FAILURE,
  FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_REQUEST,
  FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_SUCCESS,
  FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_FAILURE,

  FETCH_NEW_TOP_CONTENT_DATA_FOR_SITE_REQUEST,
  FETCH_NEW_TOP_CONTENT_DATA_FOR_SITE_SUCCESS,
  FETCH_NEW_TOP_CONTENT_DATA_FOR_SITE_FAILURE,
  FETCH_CONTENT_DATA_FOR_SITE_REQUEST,
  FETCH_CONTENT_DATA_FOR_SITE_SUCCESS,
  FETCH_CONTENT_DATA_FOR_SITE_FAILURE,
  FETCH_CONTENT_VISITS_TABLE_DATA_FOR_SITE_REQUEST,
  FETCH_CONTENT_VISITS_TABLE_DATA_FOR_SITE_SUCCESS,
  FETCH_CONTENT_VISITS_TABLE_DATA_FOR_SITE_FAILURE,
  FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_SITE_REQUEST,
  FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_SITE_SUCCESS,
  FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_SITE_FAILURE,

  FETCH_REFERRAL_DATA_FOR_SITE_REQUEST,
  FETCH_REFERRAL_DATA_FOR_SITE_SUCCESS,
  FETCH_REFERRAL_DATA_FOR_SITE_FAILURE,

  FETCH_DEVICE_DATA_FOR_SITE_REQUEST,
  FETCH_DEVICE_DATA_FOR_SITE_SUCCESS,
  FETCH_DEVICE_DATA_FOR_SITE_FAILURE,

  MAP_ANALYTICS_CONTENT_ASSET_DATA_FOR_SITE,
  MAP_ANALYTICS_CONTENT_ASSET_DATA_FOR_SITE_SUBPAGE
} from '../const/action_types.js';

import {
  DEFAULT_ANALYTICS_FILTER_SETTINGS
} from '../const/analytics.js';

import { generateHash } from 'zoomforth_zfutils/hash_utils.js';
import { getDefaultFilterSettings } from 'zoomforth_zfutils/analytics_filters_utils';

import { get as getPages } from 'zoomforth_zf/appglobals/pages.js';

const DEFAULT_VISIT_DATA = { total_visits: 0, total_unique_visits: 0, data: [], after_key: null };

const _getDefaultFilterSettingsForPage = (pageId)=> {
  if (isNil(pageId)) {
    return getDefaultFilterSettings(null, DEFAULT_ANALYTICS_FILTER_SETTINGS);
  }

  const page = getPages().get(pageId);

  if (!page) {
    throw new Error('Could not find a page with that ID');
  }

  return getDefaultFilterSettings(page.get('createdAtTs'), DEFAULT_ANALYTICS_FILTER_SETTINGS);
};

// Create a cache key such that we fetch data only when the filters change
const _createCacheKey = (state, objectId)=> {
  const {
    startDate,
    endDate,
    shouldIgnoreZfUsers,
    shouldIgnoreAnonymousUsers,
    filteredEmailDomains,
    shorterThanDuration,
    longerThanDuration
  } = getAnalyticsFiltersForPageId(state, objectId);

  // preprocess email domains so the hash can be deterministic while also not
  // caring about ordering of these domains.
  filteredEmailDomains.sort();

  return generateHash({
    objectId,
    // we want a predictable string for datetimes for a cache key
    startDate: startDate.format('MM/DD/YYYY'),
    endDate: endDate.format('MM/DD/YYYY'),
    shouldIgnoreZfUsers,
    shouldIgnoreAnonymousUsers,
    filteredEmailDomains,
    shorterThanDuration,
    longerThanDuration
  });
};

// unfortunately we cannot use `reselect` to memoize this selector since it
// takes an argument. we still get a massive benefit from memoizing this with lodash
const getMemoizedCacheKey = memoize((state, objectId)=> {
  return _createCacheKey(state, objectId);
});

export default (state = {}, action)=> {

  const cacheKey = (
    action.siteId ?
      getMemoizedCacheKey(state, action.siteId)
    :
      null
  );

  switch (action.type) {

  case RESET_ANALYTICS_FILTER_SETTINGS_FOR_PAGE: {
    // defaulting on initial analytics load
    const analyticsFilterForThisPageId = (
         state.analyticsFiltersByPageId[action.pageId]
      || _getDefaultFilterSettingsForPage(action.pageId)
    );

    return {
      ...state,
      analyticsFiltersByPageId: {
        ...state.analyticsFiltersByPageId,
        [action.pageId]: {
          ...analyticsFilterForThisPageId,
          ...omit(_getDefaultFilterSettingsForPage(action.pageId), ['startDate', 'endDate'])
        }
      }
    };
  }

  case UPDATE_ANALYTICS_FILTER_SETTINGS_FOR_PAGE: {
    return {
      ...state,
      analyticsFiltersByPageId: {
        ...state.analyticsFiltersByPageId,
        [action.pageId]: {
          ...getAnalyticsFiltersForPageId(state, action.pageId),
          ...action.filterSettings
        }
      }
    };
  }

  case SET_IS_SITE_DETAILS_TAB_LOADING: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        isSiteDetailsTabLoading: {
          ...state.analyticsData.isSiteDetailsTabLoading || {},
          [action.siteId]: action.isLoading
        }
      }
    };
  }

  case FETCH_SITE_VISITS_CHART_DATA_FOR_SITE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          siteVisitsChartData: isEmpty(action.data) ? DEFAULT_VISIT_DATA : action.data,
          siteVisitsChartDataError: null
        }
      }
    };
  }

  case FETCH_SITE_VISITS_CHART_DATA_FOR_SITE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          siteVisitsChartData: DEFAULT_VISIT_DATA,
          siteVisitsChartDataError: action.error
        }
      }
    };
  }

  case FETCH_SITE_VISITS_TABLE_DATA_FOR_SITE_SUCCESS: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    var cachedVisitsData = cloneDeep(cachedSiteData.siteVisitsTableData) || DEFAULT_VISIT_DATA;
    var innerData = cachedVisitsData.data || [];

    if (!isEmpty(action.data)) {
      cachedVisitsData = {
        ...action.data,
        data: innerData.concat(action.data.data)
      };
    }

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          siteVisitsTableData: cachedVisitsData,
          siteVisitsTableDataError: null
        }
      }
    };
  }

  case FETCH_NEW_SITE_VISITS_TABLE_DATA_FOR_SITE_SUCCESS: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          siteVisitsTableData: action.data,
          siteVisitsTableDataError: null
        }
      }
    };
  }

  case FETCH_SITE_VISITS_TABLE_DATA_FOR_SITE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          siteVisitsTableData: DEFAULT_VISIT_DATA,
          siteVisitsTableDataError: action.error
        }
      }
    };
  }

  case FETCH_VISIT_SESSION_DATA_FOR_SITE_REQUEST: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedVisitsData = cachedSiteData.visitsData || {};
    const cachedSessionData = cachedVisitsData[action.pageSessionUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          visitsData: {
            ...cachedVisitsData,
            [action.pageSessionUuid]: {
              visitSessionData: {},
              visitSessionDataError: null,
              isFetchingVisitSessionData: true
            }
          },
          currentVisitSessionKey: action.pageSessionUuid
        }
      }
    };
  }

  case FETCH_VISIT_SESSION_DATA_FOR_SITE_SUCCESS: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedVisitsData = cachedSiteData.visitsData || {};
    const cachedSessionData = cachedVisitsData[action.pageSessionUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          visitsData: {
            ...cachedVisitsData,
            [action.pageSessionUuid]: {
              ...cachedSessionData,
              visitSessionData: {
                ...action.data
              },
              visitSessionDataError: null,
              isFetchingVisitSessionData: false
            }
          },
          currentVisitSessionKey: action.pageSessionUuid
        }
      }
    };
  }

  case FETCH_VISIT_SESSION_DATA_FOR_SITE_FAILURE: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedVisitsData = cachedSiteData.visitsData || {};
    const cachedSessionData = cachedVisitsData[action.pageSessionUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          visitsData: {
            ...cachedVisitsData,
            [action.pageSessionUuid]: {
              ...cachedSessionData,
              visitSessionData: {},
              visitDataError: action.error,
              isFetchingVisitSessionData: false
            }
          },
          currentVisitSessionKey: action.pageSessionUuid
        }
      }
    };
  }

  case FETCH_TOP_SUBPAGES_DATA_FOR_SITE_REQUEST: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopSubpagesDataLoading: true,
          topSubpagesData: [],
          topSubpagesDataError: null
        }
      }
    };
  }

  case FETCH_TOP_SUBPAGES_DATA_FOR_SITE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopSubpagesDataLoading: false,
          topSubpagesData: action.data,
          topSubpagesDataError: null
        }
      }
    };
  }

  case FETCH_TOP_SUBPAGES_DATA_FOR_SITE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopSubpagesDataLoading: false,
          topSubpagesData: [],
          topSubpagesDataError: action.error
        }
      }
    };
  }

  case FETCH_NEW_TOP_CONTENT_DATA_FOR_SITE_REQUEST: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopContentDataLoading: true,
          topContentData: [],
          topContentDataError: null,
          contentData: {}
        }
      }
    };
  }

  case FETCH_NEW_TOP_CONTENT_DATA_FOR_SITE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopContentDataLoading: false,
          topContentData: action.data,
          topContentDataError: null
        }
      }
    };
  }

  case FETCH_NEW_TOP_CONTENT_DATA_FOR_SITE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isTopContentDataLoading: false,
          topContentData: [],
          topContentDataError: action.error,
          contentData: {}
        }
      }
    };
  }

  case FETCH_SUBPAGE_DATA_FOR_SITE_REQUEST: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {},
              subpageDataError: null,
              isFetchingSubpageData: true
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_DATA_FOR_SITE_SUCCESS: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    const assetIdsByType = action.assetIdsByType;

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: action.data,
              subpageDataError: null,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_DATA_FOR_SITE_FAILURE: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {},
              subpageDataError: action.error,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_REQUEST: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {},
              subpageDataError: null,
              isFetchingSubpageData: true
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_SUCCESS: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = get(cachedSubpagesData, `${action.subpageId}.subpageData`) || {};
    const cachedSubpageVisitEventsData = cachedSubpageData.visitEventsForSubpage || {};
    var visitEventsForSubpage = cachedSubpageVisitEventsData;
    var innerData = visitEventsForSubpage.data || [];

    if (!isEmpty(action.data)) {
      visitEventsForSubpage = {
        ...visitEventsForSubpage,
        ...action.data,
        data: innerData.concat(action.data.data)
      };
    }

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              subpageData: {
                ...cachedSubpageData,
                visitEventsForSubpage
              },
              subpageDataError: null,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_FAILURE: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageDataError: action.error,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_REQUEST: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {
                ...cachedSubpageData.subpageData,
              },
              subpageDataError: null,
              isFetchingSubpageData: true
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_SUCCESS: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {
                ...cachedSubpageData.subpageData,
                visitEventsForSubpage: action.data
              },
              subpageDataError: null,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_NEW_SUBPAGE_VISITS_TABLE_DATA_FOR_SITE_FAILURE: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {
                ...cachedSubpageData.subpageData,
              },
              subpageDataError: action.error,
              isFetchingSubpageData: false
            }
          },
          currentSubpageKey: action.subpageId
        }
      }
    };
  }

  case FETCH_REFERRAL_DATA_FOR_SITE_REQUEST: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isReferralDataForSiteLoading: true,
          referralData: [],
          referralDataError: null
        }
      }
    };
  }

  case FETCH_REFERRAL_DATA_FOR_SITE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isReferralDataForSiteLoading: false,
          referralData: action.data,
          referralDataError: null
        }
      }
    };
  }

  case FETCH_REFERRAL_DATA_FOR_SITE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isReferralDataForSiteLoading: false,
          referralData: [],
          referralDataError: action.error
        }
      }
    };
  }

  case FETCH_DEVICE_DATA_FOR_SITE_REQUEST: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isDeviceDataForSiteLoading: true,
          deviceData: {},
          deviceDataError: null
        }
      }
    };
  }

  case FETCH_DEVICE_DATA_FOR_SITE_SUCCESS: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isDeviceDataForSiteLoading: false,
          deviceData: action.data,
          deviceDataError: null
        }
      }
    };
  }

  case FETCH_DEVICE_DATA_FOR_SITE_FAILURE: {
    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...state.analyticsData[cacheKey] || {},
          isDeviceDataForSiteLoading: false,
          deviceData: {},
          deviceDataError: action.error
        }
      }
    };
  }

  case FETCH_CONTENT_DATA_FOR_SITE_REQUEST: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              contentData: {},
              contentDataError: null,
              isFetchingContentData: true
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_DATA_FOR_SITE_SUCCESS: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentData: action.data,
              contentDataError: null,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_DATA_FOR_SITE_FAILURE: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentDataError: action.error,
              isFetchingContentData: null
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_VISITS_TABLE_DATA_FOR_SITE_REQUEST: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentDataError: null,
              isFetchingContentData: true
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_VISITS_TABLE_DATA_FOR_SITE_SUCCESS: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};
    const innerContentData = cachedThisContentData.contentData || {};
    var visitsForContent = innerContentData.visitsForContent || {};
    var innerVisitsData = visitsForContent.data || [];

    if (!isEmpty(action.data)) {
      visitsForContent = {
        ...get(action, 'data.visitsForContent', {}),
        data: innerVisitsData.concat(get(action, 'data.visitsForContent.data', []))
      };
    }

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              ...action.data,
              contentData: {
                ...cachedThisContentData.contentData,
                visitsForContent
              },
              contentDataError: null,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_CONTENT_VISITS_TABLE_DATA_FOR_SITE_FAILURE: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentDataError: action.error,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_SITE_REQUEST: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              ...cachedThisContentData,
              contentDataError: null,
              isFetchingContentData: true
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_SITE_SUCCESS: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.contentData || {};
    const cachedThisContentData = cachedContentData[action.contentUuid] || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              contentData: {
                ...cachedThisContentData.contentData,
                ...action.data
              },
              contentDataError: null,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case FETCH_NEW_CONTENT_VISITS_TABLE_DATA_FOR_SITE_FAILURE: {
    const cachedSiteData = state.analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.contentData || {};

    return {
      ...state,
      analyticsData: {
        ...state.analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          contentData: {
            ...cachedContentData,
            [action.contentUuid]: {
              contentData: {},
              contentDataError: action.error,
              isFetchingContentData: false
            }
          },
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  case MAP_ANALYTICS_CONTENT_ASSET_DATA_FOR_SITE_SUBPAGE: {

    const { assetDataByAssetType } = action;

    const { analyticsData } = state;
    const cachedSiteData = analyticsData[cacheKey] || {};
    const cachedSubpagesData = cachedSiteData.subpagesData || {};
    const cachedSubpageData = cachedSubpagesData[action.subpageId] || {};
    const cachedSubpageAllData = cachedSubpageData.subpageData || {};
    const cachedSubpageContentData = cachedSubpageAllData.topContentOnSubpage || {};

    const updatedContentData = map(cachedSubpageContentData, (content)=> {
      const { asset_id, asset_type } = content;

      if (!isNil(asset_type) && !isNil(asset_id)) {
        const assetData = get(assetDataByAssetType, `${asset_type}.assetData.${asset_id}`);

        return { ...content, asset: assetData };
      }

      return content;
    });

    return {
      ...state,
      analyticsData: {
        ...analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          subpagesData: {
            ...cachedSubpagesData,
            [action.subpageId]: {
              ...cachedSubpageData,
              subpageData: {
                ...cachedSubpageAllData,
                topContentOnSubpage: {
                  ...cachedSubpageContentData,
                  ...updatedContentData
                }
              },
              subpageDataError: null,
              isFetchingSubpageData: false
            }
          },
        }
      }
    };
  }

  case MAP_ANALYTICS_CONTENT_ASSET_DATA_FOR_SITE: {
    const { assetDataByAssetType } = action;

    const { analyticsData } = state;
    const cachedSiteData = analyticsData[cacheKey] || {};
    const cachedContentData = cachedSiteData.topContentData || [];

    const updatedContentData = map(cachedContentData, (content)=> {
      const { asset_id, asset_type } = content;

      if (!isNil(asset_type) && !isNil(asset_id)) {
        const assetData = get(assetDataByAssetType, `${asset_type}.assetData.${asset_id}`);

        return { ...content, asset: assetData };
      }

      return content;
    });

    return {
      ...state,
      analyticsData: {
        ...analyticsData,
        [cacheKey]: {
          ...cachedSiteData,
          topContentData: updatedContentData,
          currentContentKey: action.contentUuid
        }
      }
    };
  }

  default:
    return state;
  }
};



export const resetCachedAnalyticsDataForSite = (state, siteId) => {
  const analyticsDataForSite = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}`, {});
  set(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}`, {
    // we still want our current view keys
    ...analyticsDataForSite,

    siteVisitsChartData: {},
    siteVisitsTableData: {},
    visitsData: {},

    topSubpagesData: [],
    subpagesData: {},

    topContentData: [],
    contentData: {},

    referralData: [],
    deviceData: {}
  });
};

/******************************************************************************
 * Filter Selectors
 *****************************************************************************/

export const getAnalyticsFiltersForPageId = (state, pageId)=>
  get(state.analyticsFiltersByPageId, pageId, _getDefaultFilterSettingsForPage(pageId));

export const getIsSiteDetailsTabLoading = (state, pageId)=>
  get(state.analyticsData, `isSiteDetailsTabLoading.${pageId}`, false);

/******************************************************************************
 * Data Selectors
 *****************************************************************************/

// Site visits chart data

export const getSiteVisitsChartDataForSite = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.siteVisitsChartData`, DEFAULT_VISIT_DATA);

export const getSiteVisitsChartDataForSiteError = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.siteVisitsChartDataError`, null);

// Site visits table data

export const getSiteVisitsTableDataForSite = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.siteVisitsTableData`, DEFAULT_VISIT_DATA);

export const getSiteVisitsTableDataForSiteError = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.siteVisitsTableDataError`, null);

// Cached visit session data by pageSessionUuid

export const getIsVisitSessionDataForSiteLoading = (state, siteId, pageSessionUuid)=> {
  if (!pageSessionUuid) {
    pageSessionUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentVisitSessionKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.visitsData.${pageSessionUuid}.isFetchingVisitSessionData`, false);
};

export const getVisitSessionDataForSite = (state, siteId, pageSessionUuid) => {
  if (!pageSessionUuid) {
    pageSessionUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentVisitSessionKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.visitsData.${pageSessionUuid}.visitSessionData`, {});
};

export const getVisitSessionDataForSiteError = (state, siteId, pageSessionUuid) => {
  if (!pageSessionUuid) {
    pageSessionUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentVisitSessionKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.visitsData.${pageSessionUuid}.visitSessionDataError`, null);
};

// Top subpages data

export const getIsTopSubpagesDataForSiteLoading = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.isTopSubpagesDataLoading`, false);

export const getTopSubpagesDataForSite = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.topSubpagesData`, []);

export const getTopSubpagesDataForSiteError = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.topSubpagesDataError`, null);


export const getSubpagesDataForSite = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.subpagesData`, {});

export const getContentDataForSite = (state, siteId) =>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.contentData`, {});

export const getContentDataForSiteError = (state, siteId) =>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.contentDataError`, null);

// Cached subpage data

export const getIsSubpageDataForSiteLoading = (state, siteId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.subpagesData.${subpageId}.isFetchingSubpageData`, false);
};

export const getCachedSubpageDataForSite = (state, siteId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.subpagesData.${subpageId}.subpageData`, {});
};

export const getCachedSubpageDataForSiteError = (state, siteId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.subpagesData.${subpageId}.subpageDataError`, null);
};

// Subpage visits table data

export const getSubpageVisitsTableDataForSite = (state, siteId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.subpagesData.${subpageId}.subpageData.visitEventsForSubpage`, DEFAULT_VISIT_DATA);
};

export const getSubpageVisitsTableDataForSiteError = (state, siteId, subpageId)=> {
  if (!subpageId) {
    subpageId = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentSubpageKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.subpagesData.${subpageId}.subpageData.visitEventsForSubpageError`, null);
};

// Top content data

export const getIsTopContentDataForSiteLoading = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.isTopContentDataLoading`, false);

export const getTopContentDataForSite = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.topContentData`, []);

export const getTopContentDataForSiteError = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.topContentDataError`, null);

// Cached content data by contentUuid

export const getIsContentDataForSiteLoading = (state, siteId, contentUuid)=> {
  if (!contentUuid) {
    contentUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentContentKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.contentData.${contentUuid}.isFetchingContentData`, false);
};

export const getCachedContentDataForSite = (state, siteId, contentUuid)=> {
  if (!contentUuid) {
    contentUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentContentKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.contentData.${contentUuid}.contentData`, {});
};

export const getCachedContentDataForSiteError = (state, siteId, contentUuid)=> {
  if (!contentUuid) {
    contentUuid = get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.currentContentKey`, null);
  }
  return get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.contentData.${contentUuid}.contentDataError`, null);
};

// Referrals data

export const getIsReferralDataForSiteLoading = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.isReferralDataForSiteLoading`, false);

export const getReferralDataForSite = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.referralData`, []);

export const getReferralDataForSiteError = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.referralDataError`, null);

// Device data

export const getIsDeviceDataForSiteLoading = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.isDeviceDataForSiteLoading`, false);

export const getDeviceDataForSite = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.deviceData`, {});

export const getDeviceDataForSiteError = (state, siteId)=>
  get(state.analyticsData, `${getMemoizedCacheKey(state, siteId)}.deviceDataError`, null);
